import { useEffect, useRef, useState } from 'react';

import {
  TUseScrollFadeOptions,
  TUseScrollFadeResult,
} from '@/shared/components/ui/scroll-fade/use-scroll-fade/use-scroll-fade.types';

export const useScrollFade = ({
  enabled = true,
}: TUseScrollFadeOptions = {}): TUseScrollFadeResult => {
  const ref = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) {
      setFade((prev) =>
        !prev.top && !prev.bottom ? prev : { top: false, bottom: false },
      );
      return;
    }

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const top = scrollTop > 0;
      const bottom = Math.ceil(scrollTop + clientHeight) < scrollHeight;
      setFade((prev) =>
        prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
      );
    };

    update();
    element.addEventListener('scroll', update, { passive: true });
    // Content grows async (loaded fields/targets) without resizing the
    // viewport, so watch subtree mutations alongside size changes.
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(element, { childList: true, subtree: true });

    return () => {
      element.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [enabled]);

  return { ref, fadeTop: fade.top, fadeBottom: fade.bottom };
};
